import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { PartnerSelector } from '@/components/PartnerSelector';
import { paymentMethodApi } from '@/lib/api/paymentMethodApi';
import { CreatePaymentMethodRequest, PaymentMethodErrorResponse, PartnerDto } from '@/lib/api/types';

// Expanded payment method types
const CARD_TYPES = [
  'Visa',
  'MasterCard',
  'AMEX',
  'Discover',
  'DinersClub',
  'JCB',
  'Maestro',
  'CarteBancaire',
] as const;
const TRANSFER_TYPES = ['ProPayToProPay', 'SEPA'] as const;
const BANK_TYPES = ['Checking', 'Savings'] as const;
const PAYMENT_METHOD_TYPES = [
  ...CARD_TYPES,
  ...TRANSFER_TYPES,
  ...BANK_TYPES,
] as const;

const isCardType = (t?: string) => (t ? (CARD_TYPES as readonly string[]).includes(t) : false);
const isBankType = (t?: string) => (t ? (BANK_TYPES as readonly string[]).includes(t) : false);

// Validation schema for payment method creation
const createPaymentMethodSchema = z
  .object({
    PayerAccountId: z.string().min(1, 'Payer Account ID is required'),
    PaymentMethodType: z.enum(PAYMENT_METHOD_TYPES, {
      required_error: 'Payment Method Type is required',
    }),
    AccountNumber: z.string().min(1, 'Account Number is required'),
    ExpirationDate: z.string().optional(), // MM/YY accepted; sent as MMYY
    AccountName: z.string().optional(),
    Description: z.string().min(1, 'Description is required'),
    Priority: z.number().min(1).max(99).optional(),
    DuplicateAction: z.enum(['0', '1']).optional(),
    AccountCountryCode: z.string().length(2, 'Country code must be 2 characters').optional(),
    Protected: z.boolean().optional(),
    RoutingNumber: z.string().optional(),
    AccountType: z.enum(BANK_TYPES).optional(),
    // Billing Information
    Address: z.string().min(1, 'Address is required'),
    City: z.string().min(1, 'City is required'),
    State: z.string().min(2, 'State is required'),
    ZipCode: z.string().min(1, 'ZIP Code is required'),
    Country: z.string().length(2, 'Country code must be 2 characters').default('US'),
    TelephoneNumber: z.string().optional(),
    Email: z.string().email('Invalid email format').optional(),
    // Partner and Auth
    partnerId: z.string().min(1, 'Partner selection is required'),
    authToken: z.string().min(1, 'Auth Token is required'),
  })
  .superRefine((data, ctx) => {
    if (isCardType(data.PaymentMethodType)) {
      const exp = (data.ExpirationDate || '').trim();
      if (!exp) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['ExpirationDate'],
          message: 'Expiration date is required for card payment methods',
        });
      } else {
        // Validate MMYY format (4 digits)
        if (exp.length !== 4 || !/^\d{4}$/.test(exp)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['ExpirationDate'],
            message: 'Invalid expiration date',
          });
        } else {
          // Validate month (01-12)
          const month = parseInt(exp.slice(0, 2), 10);
          if (month < 1 || month > 12) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['ExpirationDate'],
              message: 'Invalid expiration date',
            });
          } else {
            // Validate year and check if date is in the future
            const year = parseInt(exp.slice(2), 10) + 2000; // Convert YY to YYYY
            const currentDate = new Date();
            const currentYear = currentDate.getFullYear();
            const currentMonth = currentDate.getMonth() + 1; // getMonth() is 0-indexed
            // Check if expiration date is in the past
            if (year < currentYear || (year === currentYear && month < currentMonth)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['ExpirationDate'],
                message: 'Invalid expiration date',
              });
            }
          }
        }
      }
    }
    if (isBankType(data.PaymentMethodType)) {
      if (!data.RoutingNumber) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['RoutingNumber'],
          message: 'Routing number is required for bank accounts',
        });
      }
      if (!data.AccountType) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['AccountType'],
          message: 'Account type is required for bank accounts',
        });
      }
    }
  });

type CreatePaymentMethodFormData = z.infer<typeof createPaymentMethodSchema>;

interface CreatePaymentMethodFormProps {
  payerId: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

// Required label component with red asterisk
const RequiredLabel: React.FC<{children: React.ReactNode; className?: string}> = ({children, className}) => {
  return (
    <FormLabel className={className}>
      {children} <span className="text-red-500">*</span>
    </FormLabel>
  );
};

export function CreatePaymentMethodForm({ payerId, onSuccess, onCancel }: CreatePaymentMethodFormProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedPartner, setSelectedPartner] = React.useState<PartnerDto | null>(null);

  const form = useForm<CreatePaymentMethodFormData>({
    resolver: zodResolver(createPaymentMethodSchema),
    defaultValues: {
      PayerAccountId: payerId,
      PaymentMethodType: 'Visa',
      Priority: 1,
      DuplicateAction: '0', // 0 = Error
      AccountCountryCode: 'US',
      Protected: false,
      Country: 'US',
      AccountName: '',
      Description: '',
      partnerId: '',
      authToken: '',
    },
    // Only validate on submit (removed onBlur validation)
  });

  const createPaymentMethodMutation = useMutation({
    mutationFn: async (data: CreatePaymentMethodFormData) => {
      if (!selectedPartner || !selectedPartner.billerId) {
        throw new Error('Partner with valid billerId is required');
      }

      const request: CreatePaymentMethodRequest = {
        PayerAccountId: data.PayerAccountId,
        PaymentMethodType: data.PaymentMethodType,
        AccountNumber: data.AccountNumber,
        // Normalize expiration date: accept MM/YY in UI, send MMYY to API
        ExpirationDate: data.ExpirationDate
          ? data.ExpirationDate.replace('/', '')
          : undefined,
        Description: data.Description,
        Priority: data.Priority,
        DuplicateAction: data.DuplicateAction,
        AccountCountryCode: data.AccountCountryCode,
        Protected: data.Protected,
        RoutingNumber: data.RoutingNumber,
        AccountType: data.AccountType,
        BillingInformation: {
          Address: data.Address,
          City: data.City,
          State: data.State,
          ZipCode: data.ZipCode,
          Country: data.Country,
          TelephoneNumber: data.TelephoneNumber,
          Email: data.Email,
        },
        ...(data.AccountName?.trim() ? { AccountName: data.AccountName.trim() } : {}),
      };

      return paymentMethodApi.createPaymentMethod(
        payerId,
        request,
        selectedPartner.billerId, // billerId from selected partner
        data.authToken
      );
    },
    onSuccess: (data) => {
      toast.success('Payment method created successfully!', {
        description: `Payment Method ID: ${data.PaymentMethodId}`,
      });
      queryClient.invalidateQueries({ queryKey: ['paymentMethods', payerId] });
      queryClient.invalidateQueries({ queryKey: ['adminPaymentMethods', payerId] });
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error: any) => {
      console.error('Payment method creation failed:', error);

      // Extract error message with correct case sensitivity
      const errorMessage = error?.response?.data?.RequestResult?.ResultMessage ||
                          error?.response?.data?.ErrorMessage ||
                          error?.response?.data?.ErrorCode ||
                          error?.message ||
                          'Failed to create payment method';

      // If it's a 400 Bad Request error, display the error under the Account Number field
      if (error?.response?.status === 400) {
        // Set the error with the server message
        form.setError('AccountNumber', {
          type: 'server',
          message: errorMessage
        });
      } else {
        // Show toast error for other errors
        toast.error('Payment Method Creation Failed', {
          description: errorMessage,
        });
      }
    },
  });

  const onSubmit = (data: CreatePaymentMethodFormData) => {
    if (!selectedPartner) {
      toast.error('Please select a partner');
      return;
    }

    createPaymentMethodMutation.mutate(data);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Create Payment Method</CardTitle>
          <CardDescription>
            Create a new payment method for the payer. Select a partner and provide authentication details.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Payment Method Type */}
              <FormField
                control={form.control}
                name="PaymentMethodType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Payment Method Type</FormLabel>
                    <FormControl>
                      <select
                        {...field}
                        className="w-full p-2 border rounded"
                        onChange={(e) => {
                          field.onChange(e);
                          // Auto-set Account Type when bank account type is selected
                          if (isBankType(e.target.value)) {
                            form.setValue('AccountType', e.target.value as 'Checking' | 'Savings');
                          } else {
                            // Clear Account Type when non-bank type is selected
                            form.setValue('AccountType', undefined);
                          }
                        }}
                      >
                        <optgroup label="Card Brands">
                          {Array.from(CARD_TYPES).map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </optgroup>
                        <optgroup label="Transfers">
                          {Array.from(TRANSFER_TYPES).map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </optgroup>
                        <optgroup label="Bank Accounts">
                          {Array.from(BANK_TYPES).map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </optgroup>
                      </select>
                    </FormControl>
                    <FormDescription>
                      Select a specific card brand, transfer type, or bank account.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Account Number */}
              <FormField
                control={form.control}
                name="AccountNumber"
                render={({ field }) => (
                  <FormItem>
                    <RequiredLabel>Account Number</RequiredLabel>
                    <FormControl>
                      <Input placeholder="Enter account number" {...field} />
                    </FormControl>
                    <FormDescription>
                      Credit card number or bank account number.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Expiration Date (for Card Types) */}
              {isCardType(form.watch('PaymentMethodType')) && (
                <FormField
                  control={form.control}
                  name="ExpirationDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Expiration Date</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="MM/YY"
                          value={field.value ?
                            // Format display as MM/YY if we have at least 2 digits
                            field.value.length > 2 ?
                              `${field.value.slice(0, 2)}/${field.value.slice(2)}` :
                              field.value
                            : ''
                          }
                          onKeyDown={(e) => {
                            // Allow backspace, delete, tab, escape, enter, and arrow keys
                            if ([8, 9, 27, 13, 37, 38, 39, 40, 46].includes(e.keyCode)) {
                              return;
                            }
                            // Allow numbers (0-9)
                            if ((e.keyCode >= 48 && e.keyCode <= 57) || (e.keyCode >= 96 && e.keyCode <= 105)) {
                              // Only allow up to 4 digits
                              if (field.value && field.value.length >= 4) {
                                e.preventDefault();
                              }
                              return;
                            }
                            // Prevent all other keys
                            e.preventDefault();
                          }}
                          onChange={(e) => {
                            // Strip all non-numeric characters
                            const rawValue = e.target.value.replace(/[^\d]/g, '');

                            // Limit to 4 digits
                            const limitedValue = rawValue.slice(0, 4);

                            // Update the field value (stored as MMYY without slash)
                            field.onChange(limitedValue);
                          }}
                          onBlur={field.onBlur}
                        />
                      </FormControl>
                      <FormDescription>
                        Required for card payment methods (MM/YY format).
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Routing Number (for Bank Accounts) */}
              {isBankType(form.watch('PaymentMethodType')) && (
                <FormField
                  control={form.control}
                  name="RoutingNumber"
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel>Routing Number</RequiredLabel>
                      <FormControl>
                        <Input placeholder="Enter routing number" {...field} />
                      </FormControl>
                      <FormDescription>
                        Required for bank account payment methods.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Account Type (for Bank Accounts) */}
              {isBankType(form.watch('PaymentMethodType')) && (
                <FormField
                  control={form.control}
                  name="AccountType"
                  render={({ field }) => (
                    <FormItem>
                      <RequiredLabel>Account Type</RequiredLabel>
                      <FormControl>
                        <select {...field} className="w-full p-2 border rounded">
                          <option value="">Select account type</option>
                          {Array.from(BANK_TYPES).map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </FormControl>
                      <FormDescription>
                        Required for bank account payment methods.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Account Name */}
              <FormField
                control={form.control}
                name="AccountName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Account Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter account name" {...field} />
                    </FormControl>
                    <FormDescription>
                      Name on the account (optional).
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Description */}
              <FormField
                control={form.control}
                name="Description"
                render={({ field }) => (
                  <FormItem>
                    <RequiredLabel>Description</RequiredLabel>
                    <FormControl>
                      <Input placeholder="Enter description" {...field} />
                    </FormControl>
                    <FormDescription>
                      Description for the payment method.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Billing Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Billing Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="Address"
                    render={({ field }) => (
                      <FormItem>
                        <RequiredLabel>Address</RequiredLabel>
                        <FormControl>
                          <Input placeholder="Enter address" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="City"
                      render={({ field }) => (
                        <FormItem>
                          <RequiredLabel>City</RequiredLabel>
                          <FormControl>
                            <Input placeholder="Enter city" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="State"
                      render={({ field }) => (
                        <FormItem>
                          <RequiredLabel>State</RequiredLabel>
                          <FormControl>
                            <Input placeholder="Enter state" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="ZipCode"
                      render={({ field }) => (
                        <FormItem>
                          <RequiredLabel>ZIP Code</RequiredLabel>
                          <FormControl>
                            <Input placeholder="Enter ZIP code" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="Country"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Country</FormLabel>
                          <FormControl>
                            <Input placeholder="US" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="Email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="Enter email (optional)" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="TelephoneNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone Number</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter phone number (optional)" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Partner Selection */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Authentication</CardTitle>
                  <CardDescription>
                    Select a partner and provide authentication details for payment method creation.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FormField
                    control={form.control}
                    name="partnerId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Partner</FormLabel>
                        <FormControl>
                          <PartnerSelector
                    selectedPartnerId={selectedPartner?.partnerId}
                    onPartnerSelect={(partner) => {
                      setSelectedPartner(partner);
                      field.onChange(partner?.partnerId || '');
                    }}
                    label="Partner"
                    placeholder="Select a partner"
                    required
                  />
                        </FormControl>
                        <FormDescription>
                          Select the partner that will be used for authentication.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="authToken"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Auth Token</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            placeholder="Enter authentication token"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          Authentication token for the selected partner.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              <div className="flex gap-4">
                <Button
                  type="submit"
                  disabled={createPaymentMethodMutation.isPending}
                  className="flex-1"
                >
                  {createPaymentMethodMutation.isPending ? 'Creating...' : 'Create Payment Method'}
                </Button>
                {onCancel && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onCancel}
                    disabled={createPaymentMethodMutation.isPending}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
